import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-message-bar';
import { Button, makeStyles, shorthands, mergeClasses } from '@fluentui/react-components';
import { AdsStudioPanelContainer, supportedImageExtensions, supportedVideoExtensions, FileUploadingPreview, useUploadFiles, isReachFileSizeLimit, getReachFileSizeLimitErrorMessage, } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { UploadPreviewWithCustomPreview, parseFileName } from '@bingads-webui-campaign-react/upload-file-picker';
import { DismissRegular } from '@fluentui/react-icons';

const usePanelStyles = makeStyles({
  buttonWrapper: {
    ...shorthands.padding('16px', '24px', '24px'),
  },
  button: {
    minWidth: 'auto',
    ...shorthands.padding('6px', '12px'),
    marginRight: '6px',
  },
  uploadButton: {
    width: '0px',
    ...shorthands.overflow('hidden !important'),
  },
  helpLink: {
    color: '#3073ad',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
  messageBar: {
    whiteSpace: 'normal',
  },
});

const usePreviewStyles = makeStyles({
  imageItemBox: {
    paddingBottom: '5px',
    width: '298px',
    ...shorthands.overflow('hidden'),
    boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
    marginRight: '16px',
    marginBottom: '16px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: '#faf9f8',
  },
});

export const UploadFilesPanel = (props) => {
  const {
    i18n, dataService, aid,
    logger, notificationApi, navigation, mode, openSystemUpload, globalLegacyI18n, perfMarker,
    isUploadImageUpscaleEnabled,
  } = props;
  const classes = usePanelStyles();
  const previewClasses = usePreviewStyles();
  const [errors, setErrors] = useState([]);
  const uploadInput = useRef();
  const globalI18n = useMemo(() => globalLegacyI18n.get('$/i18n!legacy/global'), [globalLegacyI18n]);

  const filePickerI18n = useMemo(() => mergeI18n(globalI18n, i18n), [globalI18n, i18n]);

  const dismiss = useCallback(() => {
    setErrors([]);
    navigation.goBack();
  }, [navigation]);

  const onRenderFooterContent = useCallback(() => (
    <div className={classes.buttonWrapper}>
      <Button className={mergeClasses('cancel', classes.button)} onClick={dismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </Button>
    </div>
  ), [classes, i18n, dismiss]);

  const onFilesAddedCallback = useCallback(() => {
    setErrors([]);
    navigation.goBack({ reload: true });
  }, [navigation]);

  const {
    previewArr,
    onFileRead,
  } = useUploadFiles({
    dataService, logger, notificationApi, i18n, aid, onFilesAddedCallback, setErrors, filePickerI18n, perfMarker, isUploadImageUpscaleEnabled,
  });

  const isOpen = useMemo(() => (previewArr.length > 0 || errors.length > 0), [errors.length, previewArr.length]);
  const supportedFileExtensions = useMemo(() => {
    if (mode === 'image') {
      return supportedImageExtensions;
    } else if (mode === 'video') {
      return supportedVideoExtensions;
    }
    return [
      ...supportedImageExtensions,
      ...supportedVideoExtensions,
    ];
  }, [mode]);

  const clearInputFile = useCallback((event) => {
    if (event) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    }
  }, []);

  const readFile = useCallback((event) => {
    const { files: fileObjects } = event.target;
    const selectedFiles = !_.isUndefined(fileObjects) && fileObjects.length > 0 && fileObjects;
    if (selectedFiles) {
      const selectedFilesCount = selectedFiles.length;
      let fileIndex;
      const errorArr = [];

      for (fileIndex = 0; fileIndex < selectedFilesCount; fileIndex += 1) {
        const fileObj = selectedFiles[fileIndex];
        const fileSizeMB = (fileObj.size / 1024 / 1024).toFixed(3);
        let reachLimit;

        if (isReachFileSizeLimit) {
          const { ext } = parseFileName(fileObj.name);
          reachLimit = isReachFileSizeLimit({ ext, fileSizeMB });
        }

        if (reachLimit) {
          errorArr.push(getReachFileSizeLimitErrorMessage({ i18n }));

          break;
        }
      }
      if (_.isEmpty(errorArr)) {
        if (logger && logger.logTrace) {
          logger.logTrace('File starts to upload', { mode });
        }
        return onFileRead(selectedFiles, () => clearInputFile(event));
      }
      clearInputFile(event);
      setErrors(errorArr);
    } // else no media picked, so do nothing

    return ''; // for testability
  }, [clearInputFile, i18n, logger, mode, onFileRead]);

  useEffect(() => {
    if (openSystemUpload) {
      uploadInput.current.click();
    }
  }, [openSystemUpload]);

  return (
    <>
      <AdsStudioPanelContainer
        dismiss={dismiss}
        isOpen={isOpen}
        headerText={i18n.getString(_TL_('Upload assets'))}
        onRenderFooterContent={onRenderFooterContent}
        className="upload-files-panel"
      >
        <>
          <UploadPreviewWithCustomPreview
            assetList={previewArr}
            PreviewComponent={FileUploadingPreview}
            customClasses={previewClasses}
          />
          {
            errors.length > 0 &&
            errors.map((errMsg, index) => (
              <MessageBar
                className={mergeClasses('error', classes.messageBar)}
                // eslint-disable-next-line react/no-array-index-key
                key={`${index}`}
                intent="warning"
              >
                <MessageBarBody>
                  {errMsg}
                  {isUploadImageUpscaleEnabled && <button className={mergeClasses('HelpTopic', classes.helpLink)} query="help:app54863">{i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))}</button>}
                </MessageBarBody>
                <MessageBarActions
                  containerAction={
                    <Button
                      onClick={() => setErrors([])}
                      aria-label="dismiss"
                      appearance="transparent"
                      icon={<DismissRegular />}
                    />
                  }
                />
              </MessageBar>
            ))
          }
        </>
      </AdsStudioPanelContainer>
      <input
        ref={uploadInput}
        aria-label={i18n.getString(_TL_('upload files'))}
        className={mergeClasses('upload-image-input', classes.uploadButton)}
        onChange={readFile}
        type="file"
        tabIndex={-1}
        multiple
        accept={supportedFileExtensions.join(',')}
      />
    </>
  );
};
UploadFilesPanel.propTypes = {
  aid: PropTypes.number.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({ gotoImagesPage: PropTypes.func }).isRequired,
  dataService: PropTypes.shape({
    uploadVideoByUrl: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  mode: PropTypes.oneOf(['image', 'video']),
  openSystemUpload: PropTypes.shape({}),
  isUploadImageUpscaleEnabled: PropTypes.bool,
};

UploadFilesPanel.defaultProps = {
  mode: null,
  openSystemUpload: null,
  isUploadImageUpscaleEnabled: false,
};
